class HeroSlideshow extends HTMLElement {
  // The two slide layouts render their <video> under different class names
  // (hero-homepage-slide-overlay vs -split). Both must be paused, played and
  // hydrated alike; selecting only the overlay class silently skipped split
  // videos, which then kept playing off-screen after navigating away.
  static VIDEO_SELECTOR = '.hero-homepage__video, .hero-homepage__split-video';

  connectedCallback() {
    this.radios = [...this.querySelectorAll('.hero-homepage__radio')];
    if (!this.radios.length) return;

    this.ac = new AbortController();
    const { signal } = this.ac;

    this.radios.forEach(radio => {
      radio.addEventListener('change', e => this.onRadioChange(e), { signal });
    });

    // Arrow buttons: click prev/next radio (wraps around)
    const prevBtn = this.querySelector('.hero-homepage__arrow--prev .carousel-arrow');
    const nextBtn = this.querySelector('.hero-homepage__arrow--next .carousel-arrow');
    if (prevBtn) prevBtn.addEventListener('click', () => this.step(-1), { signal });
    if (nextBtn) nextBtn.addEventListener('click', () => this.step(1), { signal });

    // Fade in each video once the browser has a displayable frame
    this.querySelectorAll('.hero-homepage__video').forEach(v => {
      if (v.readyState >= 3) {
        v.classList.add('is-ready');
      } else {
        v.addEventListener('canplay', () => v.classList.add('is-ready'), { signal, once: true });
      }
    });

    // Kick off video on the initially-checked slide
    const checked = this.radios.find(r => r.checked);
    if (checked) this.activateSlide(checked);

    // Swipe navigation (≤480px) — also handles autoplay pause/resume for touch devices
    let touchStartX = 0, touchStartY = 0;
    this.addEventListener('touchstart', e => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      if (this._interval) this.pauseTimer();
    }, { signal, passive: true });
    this.addEventListener('touchend', e => {
      const dx = e.changedTouches[0].clientX - touchStartX;
      const dy = e.changedTouches[0].clientY - touchStartY;
      if (window.matchMedia('(max-width: 480px)').matches && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
        this.step(dx < 0 ? 1 : -1);
      }
      if (this._interval) this.startTimer();
    }, { signal, passive: true });

    // Auto-slide — only when data-autoplay is present and reduced-motion is off
    this._interval = parseInt(this.dataset.autoplay, 10) || 0;
    if (this._interval && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.addEventListener('mouseenter', () => this.pauseTimer(), { signal });
      this.addEventListener('mouseleave', () => this.startTimer(), { signal });
      this.addEventListener('focusin',    () => this.pauseTimer(), { signal });
      this.addEventListener('focusout',   () => this.startTimer(), { signal });
      document.addEventListener('visibilitychange', () => {
        document.hidden ? this.pauseTimer() : this.startTimer();
      }, { signal });
      this.startTimer();
    }
  }

  disconnectedCallback() {
    this.pauseTimer();
    this.ac?.abort();
  }

  startTimer() {
    this.pauseTimer();
    this._timer = setTimeout(() => {
      this.step(1);
    }, this._interval);
  }

  pauseTimer() {
    clearTimeout(this._timer);
  }

  step(delta) {
    const current = this.radios.findIndex(r => r.checked);
    const next = (current + delta + this.radios.length) % this.radios.length;
    const radio = this.radios[next];
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onRadioChange(e) {
    this.querySelectorAll(HeroSlideshow.VIDEO_SELECTOR).forEach(v => v.pause());
    this.activateSlide(e.target);
    // Reset the countdown on every navigation (manual or auto)
    if (this._interval) this.startTimer();
  }

  activateSlide(radio) {
    const i = this.radios.indexOf(radio);
    const slide = this.querySelectorAll('.hero-homepage__slide')[i];
    const video = slide?.querySelector(HeroSlideshow.VIDEO_SELECTOR);
    if (!video) return;
    this.hydrateVideo(video);
    video.play().catch(() => {});
  }

  /**
   * Attach a deferred video's <source> elements and let it start buffering.
   *
   * Non-first slides render their sources as `data-src` alongside
   * `preload="none"` and no `autoplay` attribute — see the two slide snippets.
   * A muted autoplay video overrides `preload="none"` (the browser must fetch to
   * reach a playable state), which is how an off-screen hero slide came to pull
   * 9.66 MB — 65% of the page — for a slide most visitors never see.
   *
   * This is a PAGE-WEIGHT and USER-DATA fix — do not cite it as an LCP improvement.
   * CORRECTED 2026-10-07, matching the two slide snippets (corrected 2026-09-03):
   * this note used to say the video "was never blocking the hero" because the LCP
   * image finished at 343–570 ms, before the video was requested, with LCP 5.6–19.7 s
   * later. That compared a measured network timing against PSI's MODELLED LCP — two
   * different clocks — and on a real throttled link bandwidth contention is real. The
   * LCP movement came from the preload fetchpriority, the below-fold eager images and
   * the sizes correction. See rules/performance-debugging.md → "Never subtract across
   * two clocks".
   *
   * Triggered ONLY from activateSlide — never from `load` or a timer — so the
   * fetch is tied to the slide actually being shown.
   *
   * Cost of that choice: the first time a viewer reaches this slide the video is
   * unbuffered, so the brand-colour fill shows until `canplay`. That is deliberate.
   *
   * No-ops on a slide whose sources are already attached.
   */
  hydrateVideo(video) {
    // Poster first, and outside the sources guard: a `poster` attribute is fetched
    // immediately even under preload="none", so non-first slides ship it as
    // data-poster (see the two slide snippets) to keep it off the initial load.
    // Promoting it here means the still is in hand for the same slide-activation
    // moment the video starts buffering — which is the brand-colour-fill gap
    // described above, and the frame iOS Low Power Mode visitors see instead of a
    // video that will never be allowed to play.
    if (video.dataset.poster) {
      video.poster = video.dataset.poster;
      delete video.dataset.poster;
    }

    const pending = video.querySelectorAll('source[data-src]');
    if (!pending.length) return;

    pending.forEach(source => {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    });

    // load() honours preload="none" and would fetch nothing until play(); the
    // point of hydrating early is to have the slide buffered before it is shown.
    video.preload = 'auto';
    video.load();
  }
}

customElements.define('hero-slideshow', HeroSlideshow);
